/* eslint-disable jsx-a11y/anchor-is-valid */
import "styles/BillListPage.scss";

import { useQuery } from "@tanstack/react-query";
import { BillList } from "components/BillList";
import { BillSmootherDefault } from "components/BillSmoother/BillSmootherDefault";
import { BillSmootherSummary } from "components/BillSmoother/BillSmootherSummary";
import { CurrentYearCostsSection } from "components/CurrentYearCostsSection";
import { NoBillDataFallback } from "components/fallback/NoBillDataFallback";
import { Loader } from "components/Loader";
import { getBillingBalance, getInvoiceTimeline } from "queries/billing";
import { getBillSmootherConfig } from "queries/payment_smoother";
import React, { useEffect, useRef, useState } from "react";
import { setBillSmootherConfig } from "reducers/billSmoother";
import { useAppDispatch, useAppSelector } from "reduxUtils/hook";
import { jwtClient } from "utils/jwtClient";
import { refetchOnScroll } from "utils/refetchOnScroll";
import { sendAnalyticsEvent } from "utils/sendAnalyticsEvent";

/**
 * @description A list of all of a customers bills from most recent to the least recent
 */
export const BillListPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { billingEntityData } = useAppSelector(
    (store) => store.billingEntityData,
  );
  const { customerNumber, supplyNodeRef, icpNumber, active } = useAppSelector(
    (store) => store.currentAccount,
  );
  const { billSmootherConfig: billSmootherState } = useAppSelector(
    (store) => store.billSmootherConfig,
  );

  const [storedTimelineBills, setStoredTimelineBills] = useState([]);
  const [nextEndDate, setNextEndDate] = useState(new Date());
  const [infiniteScrollCount, setInfiniteScrollCount] = useState(0);

  const currentSupplyNode = useRef(supplyNodeRef);

  const {
    data: timelineData,
    isLoading: isTimelineLoading,
    isFetching: isTimelineFetching,
  } = useQuery(
    ["invoiceTimeline", { nextEndDate }],
    () => getInvoiceTimeline(nextEndDate, customerNumber, jwtClient),
    {
      enabled: Boolean(customerNumber) && Boolean(active),
      keepPreviousData: true,
    },
  ); // This returns invoices that have been sent to the customer already. There is a gap between collecting the data for an invoice and the send date, so the latest bill may not display for several days after the relevant period.

  const { data: billSmootherConfig } = useQuery(
    ["billSmootherConfig"],
    () => getBillSmootherConfig(billingEntityData?.id, jwtClient),
    { enabled: Boolean(billingEntityData) },
  );

  const { data: billSmootherBalance } = useQuery(
    ["billSmootherBalance", { customerNumber, jwtClient }],
    getBillingBalance,
    { enabled: Boolean(billSmootherConfig) && billSmootherConfig.enrolled },
  );

  useEffect(() => {
    if (!billSmootherState && billSmootherConfig) {
      dispatch(setBillSmootherConfig({ billSmootherConfig }));
    }
  }, [billSmootherConfig]);

  useEffect(() => {
    // For users with multiple accounts
    // We reset and retrigger the API when the user changes accounts.
    if (currentSupplyNode.current !== supplyNodeRef) {
      setNextEndDate(new Date());
      setStoredTimelineBills([]);
      currentSupplyNode.current = supplyNodeRef;
    }
  }, [supplyNodeRef]);

  useEffect(() => {
    let nextDate = nextEndDate;
    if (timelineData?.length) {
      sendAnalyticsEvent("scroll", {
        icpNumber,
        scroll_number: infiniteScrollCount,
      });
      setInfiniteScrollCount(infiniteScrollCount + 1);
      nextDate = new Date(
        timelineData[timelineData.length - 1].attributes.invoice_date,
      );

      const timelineBills = [...timelineData];
      // This removes a duplicate bill if the last listed invoice date is included in the next lot of bills
      const nextDateIsInTimeline =
        new Date(timelineBills[0].attributes.invoice_date).getTime() ===
        nextEndDate.getTime();

      if (nextDateIsInTimeline) {
        timelineBills.shift();
      }

      setStoredTimelineBills(storedTimelineBills.concat(timelineBills));
    }
    // Ensures event listener actually dismounts and updates timelineData query key to trigger refetch
    return refetchOnScroll(timelineData, setNextEndDate, nextDate);
  }, [timelineData]);

  return (
    <div className="page_section">
      <section className="page_section__column page_section__column--1">
        <CurrentYearCostsSection
          billingEntityData={billingEntityData}
          cardChildren={!billSmootherState?.enrolled && <BillSmootherDefault />}
        />
        {billSmootherState?.enrolled && (
          <BillSmootherSummary
            billSmootherBalance={billSmootherBalance?.total_credits?.amount}
          />
        )}
      </section>
      <section className="page_section__column page_section__column--2">
        <h2 className="page__heading">Bills</h2>
        {!customerNumber && <NoBillDataFallback />}
        <nav>
          <ul className="customer_tools_list">
            {isTimelineLoading && active ? (
              <li
                className="loading-section"
                data-testid="list-loading-indicator"
              >
                <Loader />
              </li>
            ) : (
              <>
                {billingEntityData && <BillList bills={storedTimelineBills} />}
                <li
                  className={
                    isTimelineFetching
                      ? "loading-section"
                      : "loading-section--invisible"
                  }
                  data-testid="timeline-fetching-loading-indicator"
                >
                  <Loader />
                </li>
              </>
            )}
          </ul>
        </nav>
      </section>
    </div>
  );
};
